import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';

/**
 * PeekContext — page-scoped "Find in page" (like Cmd+F in the browser / Ctrl+F in Excel).
 *   • Highlights every occurrence of the query inside the current section.
 *   • Shows N of M count. Enter jumps to the next match, Shift+Enter to the previous.
 *   • Escape closes. Query is cleared automatically when the active section changes.
 *
 * The consumer wires a ref to the scope element (usually <main>) via `setScope`.
 */
const PeekContext = createContext({
  open: false,
  query: '',
  count: 0,
  index: 0,
  setOpen: () => {},
  setQuery: () => {},
  setScope: () => {},
  next: () => {},
  prev: () => {},
  reset: () => {}
});

export function usePeek() {
  return useContext(PeekContext);
}

export function PeekProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [count, setCount] = useState(0);
  const [index, setIndex] = useState(0);

  const scopeRef = useRef(null);
  const marksRef = useRef([]);

  const setScope = useCallback((el) => {
    scopeRef.current = el;
  }, []);

  const clearHighlights = useCallback(() => {
    const root = scopeRef.current;
    if (!root) return;
    root.querySelectorAll('mark.peek-hit').forEach((m) => {
      const parent = m.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(m.textContent || ''), m);
      parent.normalize();
    });
    marksRef.current = [];
  }, []);

  const setActiveMark = useCallback((i) => {
    const marks = marksRef.current;
    if (!marks.length) return;
    marks.forEach((m) => m.classList.remove('peek-hit-active'));
    const target = marks[i];
    if (target) {
      target.classList.add('peek-hit-active');
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, []);

  const highlight = useCallback((q) => {
    clearHighlights();
    const root = scopeRef.current;
    const trimmed = (q || '').trim();
    if (!root || !trimmed) {
      setCount(0);
      setIndex(0);
      return;
    }

    const needle = trimmed.toLowerCase();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        const tag = parent.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        if (parent.closest('[data-peek-exclude]')) return NodeFilter.FILTER_REJECT;
        const val = node.nodeValue;
        if (!val || !val.trim()) return NodeFilter.FILTER_REJECT;
        return val.toLowerCase().includes(needle) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(escaped, 'gi');
    const marks = [];

    nodes.forEach((node) => {
      const text = node.nodeValue || '';
      const parent = node.parentNode;
      if (!parent) return;
      const frag = document.createDocumentFragment();
      let lastIdx = 0;
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(text)) !== null) {
        if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
        const mark = document.createElement('mark');
        mark.className = 'peek-hit';
        mark.textContent = m[0];
        frag.appendChild(mark);
        marks.push(mark);
        lastIdx = m.index + m[0].length;
      }
      if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
      parent.replaceChild(frag, node);
    });

    marksRef.current = marks;
    setCount(marks.length);
    if (marks.length) {
      setIndex(1);
      setActiveMark(0);
    } else {
      setIndex(0);
    }
  }, [clearHighlights, setActiveMark]);

  // Re-highlight whenever query changes or peek opens/closes.
  useEffect(() => {
    if (!open) {
      clearHighlights();
      setCount(0);
      setIndex(0);
      return;
    }
    // Debounce so typing feels smooth on large DOMs.
    const t = setTimeout(() => highlight(query), 80);
    return () => clearTimeout(t);
  }, [open, query, highlight, clearHighlights]);

  const next = useCallback(() => {
    const c = marksRef.current.length;
    if (!c) return;
    const nextIdx0 = index % c; // index is 1-based; nextIdx0 is 0-based next
    setActiveMark(nextIdx0);
    setIndex(nextIdx0 + 1);
  }, [index, setActiveMark]);

  const prev = useCallback(() => {
    const c = marksRef.current.length;
    if (!c) return;
    const prevIdx0 = (index - 2 + c) % c;
    setActiveMark(prevIdx0);
    setIndex(prevIdx0 + 1);
  }, [index, setActiveMark]);

  const reset = useCallback(() => {
    clearHighlights();
    setQuery('');
    setCount(0);
    setIndex(0);
  }, [clearHighlights]);

  // Global shortcut — `/` opens; Escape closes and clears.
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement && document.activeElement.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA';
      if (e.key === '/' && !inField && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // Cmd/Ctrl+F also opens peek (native find suppressed)
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        reset();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, reset]);

  const value = useMemo(
    () => ({ open, query, count, index, setOpen, setQuery, setScope, next, prev, reset }),
    [open, query, count, index, setOpen, setQuery, setScope, next, prev, reset]
  );

  return <PeekContext.Provider value={value}>{children}</PeekContext.Provider>;
}
